import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X, ChevronDown, ChevronUp } from "lucide-react";

/**
 * LiveYouTubePlayer — Bigo-style co-watch layout.
 *
 * Instead of a floating picker, this component renders NOTHING when no
 * video is queued (the parent's toolbar button owns the "load" flow via
 * `loadYouTube(url)`). Once a video is active it takes over the top ~40%
 * of the room as a wide horizontal band — matches Bigo's stream-a-video
 * layout where the shared media sits above the seat grid.
 *
 * Sync path — same as before: LiveKit data channel broadcasts
 * `youtube:{load|play|pause|seek|stop}` payloads. Late-joiners pull the
 * last known state from the room's `youtube` field.
 */

let _ytApiPromise = null;
function loadYouTubeApi() {
  if (_ytApiPromise) return _ytApiPromise;
  _ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) { resolve(window.YT); return; }
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => resolve(window.YT);
  });
  return _ytApiPromise;
}

/** Extract the 11-char YouTube video ID from any link/ID form. */
export function extractYouTubeId(input) {
  if (!input) return null;
  const s = String(input).trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
  const m = s.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}


export default function LiveYouTubePlayer({ roomName, ytState, canControl, myUserId }) {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const lastAppliedRef = useRef({ video_id: null, action: null }); // dedupe seek/play calls

  const syncOut = useCallback(async (action, position) => {
    try {
      await api.post(`/live/${roomName}/youtube`, {
        action, video_id: ytState?.video_id, position_seconds: position,
      });
    } catch { /* silent — WS will resync */ }
  }, [roomName, ytState?.video_id]);

  useEffect(() => {
    if (!ytState?.video_id) {
      setReady(false);
      setLoadError(null);
      lastAppliedRef.current = { video_id: null, action: null };
      return;
    }
    // Feedback-loop guard: once a player instance exists and this
    // update originated from *us*, don't re-apply — our own YT iframe
    // is already in the target state, and re-issuing seekTo/playVideo
    // makes the control bar flicker. The FIRST render (playerRef null)
    // must always run so the controller still sees their own player.
    const originFromMe =
      !!(ytState.controller_id && ytState.controller_id === myUserId);
    if (originFromMe && playerRef.current) return;
    // Dedupe: only re-apply on video swap or an action change for
    // subsequent updates. First load always creates the player.
    const same =
      lastAppliedRef.current.video_id === ytState.video_id &&
      lastAppliedRef.current.action === ytState.action;
    if (same && playerRef.current) return;
    lastAppliedRef.current = { video_id: ytState.video_id, action: ytState.action };

    let alive = true;
    setLoadError(null);
    // Safety net: if the YT iframe never fires onReady within 12 s
    // (bad ID, ad-blocker, network flake), surface an error so the
    // room isn't stuck on "loading.video…" forever.
    const readyTimer = setTimeout(() => {
      if (alive && !playerRef.current?.getPlayerState) {
        setLoadError("Video didn't load — check the link or try again");
      }
    }, 12000);
    (async () => {
      const YT = await loadYouTubeApi();
      if (!alive) return;
      if (!playerRef.current) {
        try {
          playerRef.current = new YT.Player(hostRef.current, {
            height: "100%",
            width: "100%",
            videoId: ytState.video_id,
            playerVars: {
              playsinline: 1,
              modestbranding: 1,
              rel: 0,
              autoplay: ytState.action === "play" ? 1 : 0,
              controls: canControl ? 1 : 0,
            },
            events: {
              onReady: (e) => {
                setReady(true);
                setLoadError(null);
                clearTimeout(readyTimer);
                try { e.target.seekTo(ytState.position || 0, true); } catch { /* ignore */ }
                if (ytState.action === "play") { try { e.target.playVideo(); } catch { /* ignore */ } }
              },
              onError: (e) => {
                // YT.PlayerError codes: 2 invalid ID, 5 HTML5 issue,
                // 100 not found / private, 101/150 embed disabled.
                const map = {
                  2:   "That video link isn't valid",
                  5:   "This video can't be played here",
                  100: "Video not found or is private",
                  101: "Owner disabled embedding for this video",
                  150: "Owner disabled embedding for this video",
                };
                setLoadError(map[e?.data] || "YouTube couldn't play this video");
                clearTimeout(readyTimer);
              },
              onStateChange: (e) => {
                if (!canControl) return;
                const YTP = window.YT.PlayerState;
                const t = e.target.getCurrentTime();
                if (e.data === YTP.PLAYING) syncOut("play", t);
                else if (e.data === YTP.PAUSED) syncOut("pause", t);
              },
            },
          });
        } catch (err) {
          clearTimeout(readyTimer);
          setLoadError(err?.message || "YouTube iframe failed to load");
        }
      } else {
        try {
          const current = playerRef.current.getVideoData?.().video_id;
          if (current !== ytState.video_id) {
            playerRef.current.cueVideoById(ytState.video_id, ytState.position || 0);
          } else {
            // Only seek if we're more than 2s out of sync — small drift
            // isn't worth a seekTo that will flash the control bar.
            try {
              const now = playerRef.current.getCurrentTime?.() || 0;
              if (Math.abs(now - (ytState.position || 0)) > 2) {
                playerRef.current.seekTo(ytState.position || 0, true);
              }
            } catch { /* ignore */ }
          }
          if (ytState.action === "play") playerRef.current.playVideo();
          else if (ytState.action === "pause") playerRef.current.pauseVideo();
        } catch { /* player unstable */ }
      }
    })();
    return () => {
      alive = false;
      clearTimeout(readyTimer);
    };
  }, [ytState?.video_id, ytState?.action, ytState?.controller_id, canControl, syncOut, myUserId]);

  async function closeYt() {
    try { await api.post(`/live/${roomName}/youtube`, { action: "stop" }); }
    catch { /* silent */ }
  }

  // No video queued → render nothing. The parent's toolbar button owns
  // the "load" flow.
  if (!ytState?.video_id) return null;

  return (
    <div
      className={`absolute z-30 left-0 right-0 bg-black border-b-2 border-[#FF007F]/60 shadow-[0_10px_30px_rgba(255,0,127,0.25)] transition-all overflow-hidden ${
        collapsed ? "top-14 h-10" : "top-14 h-[40vh] md:h-[45vh]"
      }`}
      data-testid="live-youtube-player"
    >
      {/* Bigo-style header bar */}
      <div className="flex items-center justify-between px-3 h-10 bg-gradient-to-r from-[#FF007F]/25 via-transparent to-transparent border-b border-white/10">
        <div className="flex items-center gap-2 min-w-0">
          <span className="bg-[#FF007F] text-white text-[10px] font-black px-1.5 py-0.5 flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
            CO-WATCH
          </span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-white/70 truncate">
            youtube · {ytState.video_id}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="p-1.5 bg-white/5 hover:bg-white/20 text-white/80"
            aria-label={collapsed ? "Expand player" : "Minimize player"}
            data-testid="live-youtube-collapse"
          >
            {collapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
          </button>
          {canControl && (
            <button
              onClick={closeYt}
              className="p-1.5 bg-white/5 hover:bg-[#FF007F] text-white"
              aria-label="End co-watch"
              data-testid="live-youtube-close"
              title="Stop the video for everyone"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Player surface — always mounted so `playerRef` sticks across
          collapse toggles; visually clipped to zero when collapsed. */}
      <div className={`w-full ${collapsed ? "h-0" : "h-[calc(100%-2.5rem)]"} bg-black`}>
        <div ref={hostRef} className="w-full h-full" />
        {loadError && !collapsed && (
          <div
            className="absolute inset-0 top-10 flex flex-col items-center justify-center bg-black/85 gap-2 p-4 text-center"
            data-testid="live-youtube-error"
          >
            <span className="text-[#FF007F] text-xs font-mono uppercase tracking-widest">
              // co-watch.error
            </span>
            <span className="text-white/80 text-sm font-bold">{loadError}</span>
            {canControl && (
              <button
                onClick={closeYt}
                className="mt-2 px-3 py-1.5 bg-[#39FF14] hover:bg-[#5EFF3D] text-black text-[10px] font-black uppercase tracking-widest"
                data-testid="live-youtube-error-close"
              >
                Close co-watch
              </button>
            )}
          </div>
        )}
        {!ready && !loadError && !collapsed && (
          <div className="absolute inset-0 top-10 flex items-center justify-center bg-black/60 pointer-events-none">
            <span className="text-white/60 text-xs font-mono animate-pulse">// loading.video…</span>
          </div>
        )}
      </div>
    </div>
  );
}
